<template>
  <section class="add-patient-preop">
    <Breadcrumbs :items="breadcrumbs">
      <div class="consent-link">
        <router-link :to="{name: 'patient-consent'}">
          <rp-sprite class="consent-icon" name="info"/>
          View informed consent
        </router-link>
      </div>
    </Breadcrumbs>

    <div class="surgeon-select panel"
      v-if="showSurgeon">
      <rp-select
        v-model="surgeonId"
        placeholder="Please select the operating surgeon">
        <option v-for="surgeon in surgeons" :key="surgeon.id" :value="surgeon.id">
          {{surgeon.profile.first_name + " " + surgeon.profile.last_name}}
        </option>
      </rp-select>
    </div>

    <div class="surgeon-warning panel"
      v-if="showWarning">
      <span>This surgeon's questionnaire preferences do not include a pre-operative questionnaire.
        For surgeons, please check your preferences. For nurses, please contact the surgeon.</span>
    </div>

    <div v-if="showPatientForm">
      <rp-patient-form class="patient-form" 
        v-model="form"      
        :genders="genders"
        :locales="locales"
        :engagementTypes="engagementTypes"
        :targetRefractions="targetRefractions"
        @submit="createPatient"
      />
    </div>

    <div v-if="questionnaire">
      <router-view 
        @onQuestionnaireCompleted="onQuestionnaireCompleted"
      />
    </div>

    <div class="button-row"
      v-if="isComplete">
      <rp-button type="primary" @click="onAddAnother">Add Another</rp-button>
      <rp-button type="primary" @click="onGoToAllPatients">Go to All Patients</rp-button>
    </div>

  </section>
</template>

<script>
import page from '@/mixins/page'
import Breadcrumbs from '@/components/Breadcrumbs'
import Sprite from 'raypro_ui/src/components/ui/Sprite'
import Select from 'raypro_ui/src/components/ui/inputs/StyledSelect'
import Button from 'raypro_ui/src/components/ui/inputs/Button'
import PatientForm from 'raypro_ui/src/components/ui/forms/Patient_v2'
import { createPatient } from 'raypro_ui/src/api/users'
import { getLocales } from 'raypro_ui/src/api/locales'
import { getEngagementTypes } from 'raypro_ui/src/api/engagement_types'
import { getTargetRefractions } from 'raypro_ui/src/api/target_refractions'
import { getPreopForSurgeon } from 'raypro_ui/src/api/questionnaire'
import { getPreopPatientQuestionnaires, createPreopPatientQuestionnaire } from '@/api/questionnaire'
import { getPatientByEmail, getAssociatedSurgeons } from 'raypro_ui/src/api/users'
import { mapGetters } from 'vuex'


export default {
  data() {
    return {
      form: {},
      genders: [
        { label: "Male", value: "M" },
        { label: "Female", value: "F" },
        { label: "Prefer not to say", value: "O" }
      ],
      surgeons: [],
      locales: [],
      engagementTypes: [],
      targetRefractions: [],
      breadcrumbs: [{ label: "Add Patient & Pre-Op" }],
      title: "Add Patient & Pre-Op",
      surgeonId: '',
      patient: null,
      questionnaire: null,
      hasPreop: false,
      isLoaded: false,
      isChanged: false,
      isComplete: false,
    };
  },

  mixins: [page],

  computed: {
    ...mapGetters(['authUser', 'isSurgeon']),

    showSurgeon() {
      return this.isLoaded && this.isSurgeon === false && this.surgeons.length > 1 &&
        this.questionnaire === null
    },

    showWarning() {
      return (this.isLoaded && this.surgeons.length === 0) || 
        (this.isChanged && this.hasPreop === false)
    },

    showPatientForm() {
      return this.isLoaded && this.surgeonId && this.hasPreop && this.patient == null
    },

    userPayload() {
      const form = this.form;

      return {
        patient: {
          email: form.email,
          locale_id: form.locale,
          patient_id: form.patient_id,
          created_by: this.$store.getters.authUser.id,
          target_refraction_id: form.target_refraction_id,
          profile: {
            first_name: form.first_name,
            last_name: form.last_name,
            gender: form.gender,
            dob: form.dob,
            phone: form.phone,
            engagement_type_id: form.engagement_type_id
          }
        }
      };
    }
  },

  components: {
    'rp-patient-form': PatientForm,
    'rp-sprite': Sprite,
    'rp-select': Select,
    'rp-button': Button,
    Breadcrumbs
  },

  async mounted() {
    await this.getSurgeons()
    await this.getLocales()
    await this.getEngagementTypes()
    await this.getTargetRefractions()
    this.isLoaded = true
  },

  methods: {
    async getPatient() {
      const email = this.form.email
      const {data} = await getPatientByEmail(email)
      return (data.length > 0) ? data[0] : null
    },

    async getPreopForPatient(patientId) {
      const {data} = await getPreopPatientQuestionnaires(patientId)
      return (data.length > 0) ? data[0] : null
    },

    async createPatient() {
      let patient = await this.getPatient()
      if (patient == null) {
        const {data} = await createPatient(this.userPayload)
        patient = data
      }
      this.patient = patient

      let questionnaire = await this.getPreopForPatient(this.patient.id)
      if (questionnaire == null) {
        const {data} = await createPreopPatientQuestionnaire(this.patient.id, {surgeon_id: this.surgeonId})
        questionnaire = data
      }
      this.questionnaire = questionnaire

      this.$router.push({
        name: 'preop-patient-questionnaire',
        params: {
          id: this.patient.id,
          questionnaireId: this.questionnaire.id
        }
      })
    },

    async getSurgeons() {
      if (this.isSurgeon) {
        this.surgeons = [this.authUser]
      } else {
        const {data} = await getAssociatedSurgeons({userId: this.authUser.id})
        this.surgeons = data
      }

      if (this.surgeons.length === 1) {
        this.surgeonId = this.surgeons[0].id
      }
    },

    async getLocales() {
      const {data} = await getLocales()
      this.locales = data
    },

    async getEngagementTypes() {
      const {data} = await getEngagementTypes()
      this.engagementTypes = data
    },

    async getTargetRefractions() {
      const {data} = await getTargetRefractions()
      this.targetRefractions = data
    },

    async resetForm() {
      this.form = {}
      this.patient = null
      this.questionnaire = null
      this.isComplete = false
    },

    async onQuestionnaireCompleted() {
      this.isComplete = true
    },

    async onAddAnother() {
      this.$router.push({name: 'add-patient-preop'})
    },

    async onGoToAllPatients() {
      this.$router.push({name: 'all-patients'})
    }
  },

  watch: {
    async $route(to, from) {
      if (to.name === 'add-patient-preop' && 
        from.name === 'preop-patient-questionnaire') {
        this.resetForm()
      }
    },

    async surgeonId() {
      if (this.surgeonId) {
        const {data} = await getPreopForSurgeon(this.surgeonId)
        this.hasPreop = data.length > 0
      } else {
        this.hasPreop = false
      }
      this.isChanged = true
    }
  }
};
</script>

<style lang="sass">
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'

  .add-patient-preop
    .consent-link
      font-size: larger
      .consent-icon
        width: 20px
        height: 20px
        margin-top: -3px
        fill: $color-text-invert
        vertical-align: middle

    .panel
      background-color: $color-panel-bg
      padding: 0 20px 10px 20px
      border-radius: 20px
      margin-bottom: 15px

    .surgeon-warning
      padding: 15px
      font-size: 2.0em
      font-weight: bold
      color: red

    .patient-form
      max-width: 100%
      border-radius: 20px

    .button-row
      display: flex
      gap: 15px
</style>



// WEBPACK FOOTER //
// src/components/pages/Patients/AddPatientPreop.vue